/**
 * Gateway Circuit Breaker Module
 *
 * Provides automatic failover between primary (Go Gateway) and fallback (Cloudflare Workers)
 * API gateways using cockatiel circuit breaker.
 *
 * Circuit breaker resets on app restart (in-memory state).
 * Configuration is loaded from remote API on app startup with fallback to environment variables.
 */

import { circuitBreaker, ConsecutiveBreaker, handleWhen, CircuitState, CircuitBreaker } from 'cockatiel';
import * as Sentry from '@sentry/react';
import { getGatewayConfig } from '../services/gatewayConfig.service';
import { sendWithSession } from './sessionAuth';

class GatewayError extends Error {
	constructor(public readonly status: number) {
		super(`Gateway error: ${status}`);
		this.name = 'GatewayError';
	}
}

const CLOUD_API_URL = import.meta.env.REACT_APP_CLOUD_API_URL || '';

// Circuit breaker instance (created lazily, resets on app restart)
let breaker: CircuitBreaker | null = null;

/**
 * Get or create circuit breaker with dynamic config
 */
function getOrCreateBreaker(): CircuitBreaker {
	if (!breaker) {
		const config = getGatewayConfig();
		breaker = circuitBreaker(
			handleWhen((err) => err instanceof GatewayError),
			{
				halfOpenAfter: config.circuitBreaker.halfOpenAfterMs,
				breaker: new ConsecutiveBreaker(config.circuitBreaker.consecutiveFailures),
			},
		);

		// Log state changes to Sentry for monitoring
		breaker.onStateChange((state: CircuitState) => {
			const level = state === CircuitState.Open ? 'warning' : 'info';
			const currentConfig = getGatewayConfig();

			Sentry.addBreadcrumb({
				category: 'gateway',
				message: `Circuit breaker state: ${state}`,
				level: level as Sentry.Severity,
				data: {
					state,
					primaryGateway: currentConfig.primaryGatewayUrl,
					fallbackGateway: currentConfig.fallbackGatewayUrl,
				},
			});

			if (state === CircuitState.Open) {
				Sentry.captureMessage('Gateway circuit breaker opened - switching to fallback', {
					level: 'warning' as Sentry.Severity,
					tags: { component: 'gateway' },
				});
			}
		});
	}
	return breaker;
}

/**
 * Check if URL should be routed through gateway
 */
function shouldUseGateway(url: string): boolean {
	const config = getGatewayConfig();
	return Boolean(config.enabled && CLOUD_API_URL && url.startsWith(CLOUD_API_URL) && config.primaryGatewayUrl);
}

/**
 * Replace the cloud API base URL with the specified gateway URL
 */
function replaceWithGateway(url: string, gateway: string): string {
	if (CLOUD_API_URL && url.startsWith(CLOUD_API_URL)) {
		return url.replace(CLOUD_API_URL, gateway);
	}
	return url;
}

/**
 * Gateway-aware fetch with automatic circuit breaker failover
 *
 * - Routes requests through primary gateway (Go Gateway)
 * - On consecutive failures, switches to fallback gateway (Cloudflare Workers)
 * - Automatically tests primary again after 30 seconds (configurable via remote config)
 * - Resets on app restart
 * - Configuration loaded from remote API with fallback to environment variables
 *
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns Promise<Response>
 */
async function routedFetch(url: string, options?: RequestInit): Promise<Response> {
	// Skip gateway routing for non-cloud-api URLs or if gateway not configured
	if (!shouldUseGateway(url)) {
		return options !== undefined ? fetch(url, options) : fetch(url);
	}

	const config = getGatewayConfig();
	const primaryUrl = replaceWithGateway(url, config.primaryGatewayUrl);
	const errorCodes = new Set(config.circuitBreaker.errorCodes);

	try {
		// Execute request through circuit breaker
		const breakerInstance = getOrCreateBreaker();
		return await breakerInstance.execute(async () => {
			let response: Response;
			try {
				response = await fetch(primaryUrl, options);
			} catch {
				// Network-level failure (DNS, connection refused, timeout) — count as failure
				throw new GatewayError(503);
			}

			// Throw GatewayError for configured status codes — cockatiel counts this as a failure
			if (errorCodes.has(response.status)) {
				throw new GatewayError(response.status);
			}

			return response;
		});
	} catch (error) {
		// If we have a fallback gateway, use it
		if (config.fallbackGatewayUrl) {
			const fallbackUrl = replaceWithGateway(url, config.fallbackGatewayUrl);

			Sentry.addBreadcrumb({
				category: 'gateway',
				message: 'Using fallback gateway',
				level: 'warning' as Sentry.Severity,
				data: { originalUrl: url, fallbackUrl },
			});

			return fetch(fallbackUrl, options);
		}

		// No fallback available, re-throw original error
		throw error;
	}
}

/**
 * gatewayFetch: routedFetch, authenticated with the user's own session
 * credential when they have one (see sessionAuth). Without a session the
 * request is passed through unchanged.
 */
export function gatewayFetch(url: string, options?: RequestInit): Promise<Response> {
	return sendWithSession(url, options, routedFetch);
}

/**
 * Get current circuit breaker state (for debugging)
 */
export function getCircuitState(): CircuitState {
	return breaker ? breaker.state : CircuitState.Closed;
}

/**
 * Check if currently using fallback gateway
 */
export function isUsingFallback(): boolean {
	return breaker ? breaker.state === CircuitState.Open : false;
}

/**
 * Reset circuit breaker (useful when config changes)
 */
export function resetCircuitBreaker(): void {
	breaker = null;
}

// Expose to window for debugging in browser console (dev only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
	(window as any).__gatewayDebug = {
		getState: getCircuitState,
		isUsingFallback,
		getConfig: getGatewayConfig,
		resetBreaker: resetCircuitBreaker,
	};
}
